import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { PRICE_INPUT_MASK_SETTINGS } from '../../constants';
import { Bem } from '../../service/Bem/Bem';
import { CurrencyHelper } from '../../service/CurrencyHelper/CurrencyHelper';
import { BannersPriceField } from '../../typings/bannerPricesPopupState';
import { BannerPrice, PrefixPrice } from '../../typings/banners';
import { GdAdPriceCurrency } from '../../typings/graphql-grid';
import { DirectInputErrorsView } from '../DirectInputErrorsView/DirectInputErrorsView';
import { InputFieldType } from '../InputField/InputField';
import { RequiredMark } from '../RequiredMark/RequiredMark';
import { SelectDecorator } from '../SelectDecorator/SelectDecorator';

import './BannersPriceEditor2.css';
import * as keyset from './BannersPriceEditor2.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'banners-price-editor2';

type BannersPriceValueField = BannersPriceField.PRICE | BannersPriceField.PRICE_OLD;

export interface BannersPriceEditor2Props {
    bannerPrice: BannerPrice;
    onChange: (field: BannersPriceField, value: string, paths?: Array<string>) => void;
    isPriceRequired?: boolean;
    mods?: {
        [key: string]: string | boolean;
    };
}

export class BannersPriceEditor2 extends React.PureComponent<BannersPriceEditor2Props> {
    render() {
        return (
            <div className={Bem.cls({ block: BLOCK, mods: this.props.mods })}>
                <div className={classNames(Bem.cls(BLOCK, 'field-wrapper'), Bem.cls(BLOCK, 'prefix-field'))}>
                    <div className={Bem.cls(BLOCK, 'select-title')}>{i18n('prefix')}</div>

                    <div className={Bem.cls(BLOCK, 'select-content')}>{this.getPrefixPrice()}</div>
                </div>

                <div className={classNames(Bem.cls(BLOCK, 'field-wrapper'), Bem.cls(BLOCK, 'price-field'))}>
                    <div className={Bem.cls(BLOCK, 'input-title')}>
                        {i18n('price')}

                        {this.props.isPriceRequired ? <RequiredMark cls={Bem.cls(BLOCK, 'required-mark')} /> : null}
                    </div>

                    <div className={Bem.cls(BLOCK, 'input-content')}>
                        {this.getPrice(BannersPriceField.PRICE, this.onPriceChange, true)}
                    </div>
                </div>

                <div className={classNames(Bem.cls(BLOCK, 'field-wrapper'), Bem.cls(BLOCK, 'currency-field'))}>
                    <div className={Bem.cls(BLOCK, 'select-title')}>{i18n('currency')}</div>

                    <div className={Bem.cls(BLOCK, 'select-content')}>{this.getCurrency()}</div>
                </div>

                <div className={classNames(Bem.cls(BLOCK, 'field-wrapper'), Bem.cls(BLOCK, 'priceOld-field'))}>
                    <div className={Bem.cls(BLOCK, 'input-title')}>{i18n('priceOld')}</div>

                    <div className={Bem.cls(BLOCK, 'input-content')}>
                        {this.getPrice(BannersPriceField.PRICE_OLD, this.onOldPriceChange)}
                    </div>
                </div>
            </div>
        );
    }

    private getPrefixPrice() {
        const prefixValue = this.props.bannerPrice.prefix,
            prefixOptions = [
                [PrefixPrice.DEFAULT, i18n('default')],
                [PrefixPrice.FROM, i18n('from')],
                [PrefixPrice.TO, i18n('to')]
            ].map(([prefix, text]: [PrefixPrice, string]) => ({
                val: prefix,
                text: text
            }));

        return (
            <SelectDecorator
                cls={Bem.cls(BLOCK, 'select-wrap')}
                items={prefixOptions}
                val={PrefixPrice[prefixValue] || PrefixPrice.DEFAULT}
                onChange={this.onPrefixPriceChange}
                size={'n'}
                width={'max'}
                popup={{ hiding: false }}
                view={'classic'}
                theme={'normal'}
                type={'radio'}
                button={{ pin: 'brick-brick' }}
            />
        );
    }

    private getPrice(
        fieldPrice: BannersPriceValueField,
        onChange: (value: string) => void,
        autoFocus: boolean = false
    ) {
        const path = fieldPrice === 'price' ? '[0].price' : '[0].priceOld';

        return (
            <DirectInputErrorsView
                inputType={InputFieldType.MASKED_TEXT}
                cls={Bem.cls(BLOCK, 'sms-start-time')}
                text={this.props.bannerPrice[fieldPrice]}
                theme={'normal'}
                view={'classic'}
                size={'n'}
                hasClear={true}
                onChange={onChange}
                maskOptions={PRICE_INPUT_MASK_SETTINGS}
                path={path}
                autoFocus={autoFocus}
            />
        );
    }

    private getCurrency() {
        const { bannerPrice } = this.props,
            currencyValue = bannerPrice.currency,
            currencyOptions = CurrencyHelper.getCurrencyOptions();

        return (
            <SelectDecorator
                cls={Bem.cls(BLOCK, 'select-wrap')}
                items={currencyOptions}
                val={GdAdPriceCurrency[currencyValue]}
                onChange={this.onCurrencyChange}
                size={'n'}
                view={'classic'}
                theme={'normal'}
                type={'radio'}
                width={'max'}
                popup={{ hiding: false }}
                button={{ pin: 'brick-brick' }}
            />
        );
    }

    private onPriceChange = (value: string) => {
        this.props.onChange(BannersPriceField.PRICE, value, ['[0].price']);
    };

    private onOldPriceChange = (value: string) => {
        this.props.onChange(BannersPriceField.PRICE_OLD, value, ['[0].priceOld']);
    };

    private onCurrencyChange = (value: string[]) => {
        this.props.onChange(BannersPriceField.CURRENCY, value[0]);
    };

    private onPrefixPriceChange = (value: string[]) => {
        this.props.onChange(BannersPriceField.PREFIX, value[0]);
    };
}
